var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as Yup from 'yup';
import { getMatchedKey, isAddressObject, safeStringRegex, } from '../../../utils/common';
import { parse, isValid, format, startOfDay } from 'date-fns';
import { evaluateComplexCondition } from './filter';
/**
 * Normalizes a value for comparison based on its input type.
 * This ensures that a UI-formatted value (e.g., "$1,234.56") can be compared
 * correctly with a raw value from an API (e.g., 1234.56).
 *
 * @param {any} value The value to normalize.
 * @param {string} inputType The inputType of the question.
 * @returns {string} A standardized string representation for comparison.
 */
export var normalizeForComparison = function (value, inputType) {
    if (value === null || value === undefined) {
        return '';
    }
    var type = inputType.toLowerCase();
    switch (type) {
        case 'date': {
            var date = new Date(value);
            if (!isNaN(date.getTime())) {
                // Format to MM-dd-yyyy as requested
                var month = String(date.getMonth() + 1).padStart(2, '0');
                var day = String(date.getDate()).padStart(2, '0');
                var year = date.getFullYear();
                return "".concat(month, "-").concat(day, "-").concat(year);
            }
            return String(value); // Fallback for invalid dates
        }
        case 'money':
            // Strip all non-numeric characters except for the decimal point
            return String(value).replace(/[^-\d.]/g, '');
        case 'phonenumber':
            // Strip all non-digit characters
            return String(value).replace(/\D/g, '');
        case 'multiselect':
            // Sort and join arrays to create a consistent signature for comparison
            if (Array.isArray(value)) {
                return (!value.length ? ['None'] : value).sort().join(',');
            }
            return String(value);
        case 'toggle':
        case 'checkbox':
        case 'number':
        case 'autocomplete':
        case 'radio':
        case 'email':
        case 'select':
        case 'text':
        default:
            // Default to a simple string conversion
            return String(value);
    }
};
/**
 * Parses a date constraint string or number into a Date object.
 * Supports relative years (e.g., "-5y") and day offsets (e.g., 0 for today).
 * @param constraint The date constraint value (e.g., "1800-01-01", "-5y", 0).
 * @returns A Date object representing the constraint.
 */
var parseDateConstraint = function (constraint) {
    var now = new Date();
    // Set to midnight to prevent time-of-day comparison issues
    now.setHours(0, 0, 0, 0);
    if (typeof constraint === 'number') {
        // Assumes 0 means today, other numbers are day offsets from today
        now.setDate(now.getDate() + constraint);
        return now;
    }
    if (typeof constraint === 'string') {
        // Handle relative year format (e.g., "-5y", "+1y")
        var relativeYearMatch = constraint.match(/^(-?\d+)y$/);
        if (relativeYearMatch) {
            var yearsOffset = parseInt(relativeYearMatch[1], 10);
            now.setFullYear(now.getFullYear() + yearsOffset);
            return now;
        }
        // Handle standard date string (e.g., "2023-01-01")
        var date = new Date(constraint);
        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    // Fallback to the current date if parsing fails
    return now;
};
function processRefValue(value, valueType) {
    if (valueType === void 0) { valueType = 'date'; }
    if (value === null || typeof value === 'undefined' || value === '') {
        return undefined;
    }
    switch (valueType) {
        case 'number':
            return Number(value);
        case 'string':
            return String(value);
        case 'date':
        default:
            var date = new Date(value);
            return !isNaN(date.getTime()) ? value : undefined;
    }
}
export var createValidationSchema = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    var questions = _a.questions, removedQuestions = _a.removedQuestions, parentKey = _a.parentKey, values = _a.values, tempValues = _a.tempValues, apiValidationErrors = _a.apiValidationErrors, groupKey = _a.groupKey;
    if (!questions)
        return;
    var validations = {};
    var _loop_1 = function (question) {
        var questionKey = question === null || question === void 0 ? void 0 : question.questionKey;
        if (parentKey) {
            questionKey = "".concat(parentKey, ".").concat(questionKey);
        }
        var isRemoved = removedQuestions === null || removedQuestions === void 0 ? void 0 : removedQuestions.includes(questionKey);
        if (isRemoved ||
            ['sectionHeading', 'null', 'address', 'sectionHeadingEnd'].includes(question === null || question === void 0 ? void 0 : question.inputType)) {
            return "continue";
        }
        var condition = Yup.string();
        if (['select', 'string', 'text'].includes(question === null || question === void 0 ? void 0 : question.inputType.toLowerCase())) {
            condition = Yup.string().matches(safeStringRegex, 'The value can only include letters, numbers, and the following symbols: (, ), $, %, @, &');
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'autocomplete') {
            condition = condition.test('valid-autocomplete', 'Please select a valid option from the list', function (value) {
                var _a;
                if (value === null || value === undefined) {
                    return !(question === null || question === void 0 ? void 0 : question.required); // Return true only if field is NOT required
                }
                return (_a = question.options) === null || _a === void 0 ? void 0 : _a.some(function (option) { return option.value === value; });
            });
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'url') {
            condition = Yup.string().url('Please enter valid url, example: "https://www.google.com"');
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'number') {
            condition = Yup.number();
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'email') {
            condition = Yup.string().email("Please enter a valid email");
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'radio') {
            condition = Yup.mixed();
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'toggle') {
            condition = Yup.boolean();
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'checkbox') {
            condition = Yup.boolean();
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'date') {
            condition = Yup.date().transform(function (value, originalValue) {
                if (typeof originalValue === 'string') {
                    var parsedDate = parse(originalValue, 'MM-dd-yyyy', new Date());
                    return isValid(parsedDate) ? parsedDate : new Date(originalValue);
                }
                return value;
            });
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'money') {
            condition = Yup.string().transform(function (val) {
                return typeof val === 'string' ? val.replace(/,/g, '') : val;
            });
            condition = condition.test('test-money-valid', 'Please enter valid amount value', function (val) {
                if (val === null || val === undefined || val === '')
                    return true;
                return (typeof val === 'number' ||
                    (typeof val === 'string' && !/\s+/.test(val) && !isNaN(Number(val))));
            });
            condition = condition.test('test-money', 'Must be at least 0', function (val) {
                try {
                    if (val === null || val === undefined || val === '')
                        return true;
                    var valNum = Number(val);
                    return valNum > -1;
                }
                catch (e) {
                    return false;
                }
            });
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'terms_and_conditions') {
            condition = Yup.string().matches(/^agree$/i, 'You must agree to the terms and conditions');
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'multiselect') {
            condition = Yup.array()
                .of(Yup.string())
                .min(question.required ? 1 : 0, "Please select at least 1 option")
                .label((question === null || question === void 0 ? void 0 : question.question) || '');
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'coverage_card') {
            condition = Yup.mixed().test('coverage-card-validation', 'Invalid coverage card data', function (value) {
                var _a, _b, _c, _d;
                if (!value) {
                    if (question === null || question === void 0 ? void 0 : question.required) {
                        return this.createError({
                            message: 'This field is required. - please enable coverage and provide protection details'
                        });
                    }
                    return true;
                }
                if (typeof value.enabled !== 'boolean') {
                    return this.createError({
                        message: 'Coverage status must be specified'
                    });
                }
                if ((question === null || question === void 0 ? void 0 : question.required) &&
                    (!value.enabled ||
                        !((_a = value.protection) === null || _a === void 0 ? void 0 : _a.trim()) ||
                        !((_b = value.premium) === null || _b === void 0 ? void 0 : _b.trim()))) {
                    return this.createError({
                        message: 'This field is required. - please enable coverage and provide protection and premium details'
                    });
                }
                if (value.enabled && !((_c = value.protection) === null || _c === void 0 ? void 0 : _c.trim())) {
                    return this.createError({
                        message: 'Protection details are required when coverage is enabled'
                    });
                }
                if (value.enabled && !((_d = value.premium) === null || _d === void 0 ? void 0 : _d.trim())) {
                    return this.createError({
                        message: 'Protection details are required when coverage is enabled'
                    });
                }
                return true;
            });
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'table') {
            var itemSchema = createValidationSchema({
                questions: question.questions,
                removedQuestions: removedQuestions,
                parentKey: parentKey,
                values: values,
                tempValues: tempValues,
                apiValidationErrors: apiValidationErrors,
                groupKey: groupKey
            });
            condition = Yup.array().of(itemSchema);
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'group') {
            var repeatKeys = [''];
            if (question.repeatBy) {
                var value = tempValues[question.repeatBy.targetKey] ||
                    values[question.repeatBy.targetKey];
                var config = questions.find(function (q) { return q.questionKey === question.repeatBy.targetKey; });
                if (config.inputType === 'number') {
                    if (typeof value === 'number' && value > -1) {
                        repeatKeys = Array.from({ length: value }).map(function (_, i) {
                            return (i + 1).toString();
                        });
                    }
                    else {
                        repeatKeys = [];
                    }
                }
                else if (config.inputType === 'multiselect') {
                    if (Array.isArray(value)) {
                        repeatKeys = value;
                    }
                    else {
                        repeatKeys = [];
                    }
                }
            }
            repeatKeys.forEach(function (repeatKey) {
                var parentKey = question.questionKey;
                if (repeatKey) {
                    var validationSchema = createValidationSchema({
                        questions: question.questions,
                        removedQuestions: removedQuestions,
                        parentKey: "".concat(parentKey, "_").concat(repeatKey),
                        values: values,
                        tempValues: tempValues,
                        apiValidationErrors: apiValidationErrors,
                        groupKey: groupKey
                    });
                    if (validationSchema) {
                        validations["".concat(parentKey, "_").concat(repeatKey)] = validationSchema;
                    }
                }
                else {
                    condition = createValidationSchema({
                        questions: question.questions,
                        removedQuestions: removedQuestions,
                        parentKey: parentKey,
                        values: values,
                        tempValues: tempValues,
                        apiValidationErrors: apiValidationErrors,
                        groupKey: groupKey
                    });
                }
            });
            if (question.renderAs === 'multi_table' && question.targetQuestionKey) {
                var targetAllKeys = Object.keys(tempValues).filter(function (k) {
                    return new RegExp("^".concat(question.targetQuestionKey, "__[0-9]+$")).test(k);
                });
                targetAllKeys.forEach(function (key) {
                    var parentKey = question.questionKey;
                    var targetValue = tempValues[key];
                    var combineKey = "".concat(targetValue ? "".concat(parentKey, "_").concat(targetValue) : "".concat(key));
                    if (!targetValue) {
                        validations[combineKey] = Yup.string().required('This field is required.');
                        return;
                    }
                    var validationSchema = createValidationSchema({
                        questions: question.questions,
                        removedQuestions: removedQuestions,
                        parentKey: "".concat(combineKey),
                        values: values,
                        tempValues: tempValues,
                        apiValidationErrors: apiValidationErrors,
                        groupKey: groupKey
                    });
                    if (validationSchema) {
                        validations["".concat(parentKey, "_").concat(targetValue)] = validationSchema;
                    }
                });
            }
            if (question.repeatBy || question.targetQuestionKey) {
                return "continue";
            }
        }
        else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) === 'phonenumber') {
            condition = Yup.string().test('valid-length', 'Phone number must be 10 digits', function (value) {
                // If not required and empty, skip length validation
                if (!(question === null || question === void 0 ? void 0 : question.required) && (!value || value.trim() === '')) {
                    return true;
                }
                // Count digits only
                var digits = (value || '').replace(/\D/g, '');
                return digits.length >= 10;
            });
        }
        if (question.validation &&
            Array.isArray(question.validation) &&
            ((_b = question === null || question === void 0 ? void 0 : question.inputType) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== 'group' &&
            ((_c = question === null || question === void 0 ? void 0 : question.inputType) === null || _c === void 0 ? void 0 : _c.toLowerCase()) !== 'table') {
            var _loop_2 = function (rule) {
                var errorMessage = ((_d = question.uiValidation) === null || _d === void 0 ? void 0 : _d[rule.type]) || 'Invalid input.';
                switch (rule.type) {
                    case 'pattern':
                        if (rule.pattern) {
                            try {
                                if (((_e = question === null || question === void 0 ? void 0 : question.inputType) === null || _e === void 0 ? void 0 : _e.toLowerCase()) === 'money') {
                                    condition = condition.transform(function (value) {
                                        return typeof value === 'string'
                                            ? value.replace(/,/g, '')
                                            : value;
                                    });
                                }
                                else if (((_f = question === null || question === void 0 ? void 0 : question.inputType) === null || _f === void 0 ? void 0 : _f.toLowerCase()) === 'phonenumber') {
                                    condition = condition.transform(function (value) {
                                        return typeof value === 'string'
                                            ? value.replace(/\D/g, '')
                                            : value;
                                    });
                                }
                                var regex = new RegExp(rule.pattern);
                                condition = condition.matches(regex, errorMessage);
                            }
                            catch (e) {
                                console.error("Invalid regex for ".concat(questionKey, ": ").concat(rule.pattern), e);
                            }
                        }
                        break;
                    case 'year':
                        condition = condition.test('is-valid-year', errorMessage, function (value) {
                            if (!value)
                                return true;
                            var yearNum = Number(value);
                            if (isNaN(yearNum) || !/^\d{4}$/.test(String(value))) {
                                return false;
                            }
                            var currentYear = new Date().getFullYear();
                            var minYear = -Infinity;
                            var maxYear = Infinity;
                            if (rule.start !== undefined && rule.start !== null) {
                                if (String(rule.start) === '0') {
                                    minYear = currentYear;
                                }
                                else {
                                    var startMatch = String(rule.start).match(/^(-?\d+)y$/);
                                    minYear = startMatch
                                        ? currentYear + parseInt(startMatch[1], 10)
                                        : Number(rule.start);
                                }
                            }
                            if (rule.end !== undefined && rule.end !== null) {
                                if (String(rule.end) === '0') {
                                    maxYear = currentYear;
                                }
                                else {
                                    var endMatch = String(rule.end).match(/^(-?\d+)y$/);
                                    maxYear = endMatch
                                        ? currentYear + parseInt(endMatch[1], 10)
                                        : Number(rule.end);
                                }
                            }
                            return yearNum >= minYear && yearNum <= maxYear;
                        });
                        break;
                    case 'date':
                        var noUiErrorMessage = errorMessage === 'Invalid input.';
                        if (rule.start !== undefined && rule.start !== null) {
                            var parsedDate = parseDateConstraint(rule.start);
                            if (noUiErrorMessage) {
                                errorMessage = "Date must be ".concat(format(parsedDate, 'MM/dd/yyyy'), " or later.");
                            }
                            condition = condition.min(parsedDate, errorMessage);
                        }
                        if (rule.end !== undefined && rule.end !== null) {
                            var parsedDate = parseDateConstraint(rule.end);
                            if (noUiErrorMessage) {
                                errorMessage = "Date must be ".concat(format(parsedDate, 'MM/dd/yyyy'), " or earlier.");
                            }
                            condition = condition.max(parsedDate, errorMessage);
                        }
                        break;
                    case 'length':
                        if (typeof rule.min === 'number') {
                            var minMessage = ((_g = question.uiValidation) === null || _g === void 0 ? void 0 : _g.length) ||
                                "Must be at least ".concat(rule.min, " characters");
                            condition = condition.min(rule.min, minMessage);
                        }
                        if (typeof rule.max === 'number') {
                            var maxMessage = ((_h = question.uiValidation) === null || _h === void 0 ? void 0 : _h.length) ||
                                "Must be no more than ".concat(rule.max, " characters");
                            condition = condition.max(rule.max, maxMessage);
                        }
                        break;
                    case 'condition':
                        condition = condition.test('complex-condition', errorMessage, function (val) {
                            var _a, _b;
                            return !evaluateComplexCondition(rule.condition, (_a = {}, _a[groupKey] = __assign(__assign({}, tempValues), (_b = {}, _b[questionKey] = val, _b)), _a), values);
                        });
                        break;
                    case 'equal': {
                        var expectedValue_1 = rule.value;
                        var defaultMessage = "Value must be equal to ".concat(expectedValue_1);
                        var customErrorMessage = ((_j = question.uiValidation) === null || _j === void 0 ? void 0 : _j.equal) || defaultMessage;
                        condition = condition.test('is-equal-to-value', customErrorMessage, function (value) {
                            if (value === undefined || value === null) {
                                return true;
                            }
                            var v = value === true || value === 'true' ? true : value;
                            v = v === false || v === 'false' ? false : v;
                            return v === expectedValue_1;
                        });
                        break;
                    }
                }
            };
            for (var _t = 0, _u = question.validation; _t < _u.length; _t++) {
                var rule = _u[_t];
                _loop_2(rule);
            }
        }
        if (question === null || question === void 0 ? void 0 : question.required) {
            if (['string', 'text'].includes(question === null || question === void 0 ? void 0 : question.inputType.toLowerCase())) {
                condition = condition.trim('This field is required.');
            }
            condition = condition.required('This field is required.');
        }
        if (((_k = question === null || question === void 0 ? void 0 : question.pattern) === null || _k === void 0 ? void 0 : _k.toLowerCase()) === 'alphabet') {
            condition = Yup.string().matches(/^[A-Za-z ]+$/, 'Only alphabets are allowed');
        }
        else if (((_l = question === null || question === void 0 ? void 0 : question.pattern) === null || _l === void 0 ? void 0 : _l.toLowerCase()) === 'phonenumber') {
            condition = Yup.string().matches(/^[0-9+-]+$/, 'Only numbers are allowed');
        }
        if (typeof (question === null || question === void 0 ? void 0 : question.min) !== 'undefined') {
            var message = ((_m = question.uiValidation) === null || _m === void 0 ? void 0 : _m.range) ||
                "Value must be at least ".concat(question.min);
            if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'date') {
                message =
                    ((_o = question.uiValidation) === null || _o === void 0 ? void 0 : _o.range) ||
                        "Date must be at least ".concat(question.min === 'currentDate' ? 'today' : question.min);
                condition = condition
                    .min(question.min === 'currentDate'
                    ? startOfDay(new Date())
                    : startOfDay(new Date(question.min)), message)
                    .label(question.question);
            }
            else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'money') {
                condition = condition
                    .test('min-money', message, function (val) {
                    if (val === null || val === undefined || val === '')
                        return true;
                    var parsedNumber = Number(val);
                    if (isNaN(parsedNumber))
                        return true;
                    return parsedNumber >= Number(question.min);
                })
                    .label(question.question);
            }
            else {
                condition = condition
                    .min(question.min, message)
                    .label(question.question);
            }
        }
        if (typeof (question === null || question === void 0 ? void 0 : question.minRef) !== 'undefined') {
            var _v = question.minRef, targetKey = _v.targetKey, targetValueType = _v.targetValueType;
            var refValue = (_p = tempValues === null || tempValues === void 0 ? void 0 : tempValues[targetKey]) !== null && _p !== void 0 ? _p : values === null || values === void 0 ? void 0 : values[targetKey];
            var minValue = processRefValue(refValue, targetValueType);
            if (typeof minValue !== 'undefined') {
                condition = condition.min(minValue).label(question.question);
            }
        }
        if (typeof (question === null || question === void 0 ? void 0 : question.max) !== 'undefined') {
            var message = ((_q = question.uiValidation) === null || _q === void 0 ? void 0 : _q.range) || "Value must be at most ".concat(question.max);
            if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'date') {
                message =
                    ((_r = question.uiValidation) === null || _r === void 0 ? void 0 : _r.range) ||
                        "Date must be at most ".concat(question.max === 'currentDate' ? 'today' : question.max);
                condition = condition
                    .max(question.max === 'currentDate'
                    ? new Date().toISOString().split('T')[0]
                    : new Date(question.max).toISOString().split('T')[0], message)
                    .label(question.question);
            }
            else if ((question === null || question === void 0 ? void 0 : question.inputType.toLowerCase()) == 'money') {
                condition = condition
                    .test('max-money', message, function (val) {
                    if (val === null || val === undefined || val === '')
                        return true;
                    var parsedNumber = Number(val);
                    if (isNaN(parsedNumber))
                        return true;
                    return parsedNumber <= Number(question.max);
                })
                    .label(question.question);
            }
            else {
                condition = condition
                    .max(question.max, message)
                    .label(question.question);
            }
        }
        if (typeof (question === null || question === void 0 ? void 0 : question.maxRef) !== 'undefined') {
            var _w = question.maxRef, targetKey = _w.targetKey, targetValueType = _w.targetValueType;
            var refValue = (_s = tempValues === null || tempValues === void 0 ? void 0 : tempValues[targetKey]) !== null && _s !== void 0 ? _s : values === null || values === void 0 ? void 0 : values[targetKey];
            var maxValue = processRefValue(refValue, targetValueType);
            if (typeof maxValue !== 'undefined') {
                condition = condition.max(maxValue).label(question.question);
            }
        }
        if (apiValidationErrors) {
            var _apiValidationErrors = JSON.parse(JSON.stringify(apiValidationErrors));
            var allAPIErrorKeys = Object.keys(_apiValidationErrors);
            for (var _x = 0, allAPIErrorKeys_1 = allAPIErrorKeys; _x < allAPIErrorKeys_1.length; _x++) {
                var key = allAPIErrorKeys_1[_x];
                var eValue = _apiValidationErrors[key];
                if (eValue &&
                    'value' in eValue &&
                    eValue.value &&
                    eValue.message &&
                    isAddressObject(eValue.value)) {
                    var addressKeyFromError = eValue.message.match(/address\.(city|county|region|street|country|secondary|postalCode)/i);
                    if (addressKeyFromError && addressKeyFromError[1]) {
                        delete _apiValidationErrors[key];
                        _apiValidationErrors["".concat(key, ".").concat(addressKeyFromError[1])] = __assign({}, eValue);
                    }
                }
            }
            allAPIErrorKeys = Object.keys(_apiValidationErrors);
            var eKey = getMatchedKey(allAPIErrorKeys, question.questionKey) ||
                getMatchedKey(allAPIErrorKeys, questionKey);
            if (eKey) {
                var errorDetails = _apiValidationErrors[eKey];
                if ('value' in errorDetails) {
                    var errorMessage = errorDetails.message;
                    var invalidApiValue_1 = errorDetails.value;
                    if (isAddressObject(invalidApiValue_1)) {
                        var k = "".concat(eKey).split('.')[1];
                        var v = invalidApiValue_1[k];
                        if (v) {
                            invalidApiValue_1 = v;
                        }
                    }
                    if (invalidApiValue_1 === null || invalidApiValue_1 === void 0 ? void 0 : invalidApiValue_1.code) {
                        invalidApiValue_1 = invalidApiValue_1 === null || invalidApiValue_1 === void 0 ? void 0 : invalidApiValue_1.code;
                    }
                    condition = condition.test("api-validation-".concat(question.questionKey), errorMessage, function (currentValue) {
                        var normalizedCurrentValue = normalizeForComparison(currentValue, question.inputType);
                        var normalizedInvalidValue = normalizeForComparison(typeof invalidApiValue_1 === 'string'
                            ? invalidApiValue_1 === null || invalidApiValue_1 === void 0 ? void 0 : invalidApiValue_1.trim()
                            : invalidApiValue_1, question.inputType);
                        return !getMatchedKey([normalizedCurrentValue], normalizedInvalidValue);
                    });
                }
                else if ('required' in errorDetails) {
                    if (errorDetails.required) {
                        if (['string', 'text'].includes(question === null || question === void 0 ? void 0 : question.inputType.toLowerCase())) {
                            condition = condition.trim('This field is required.');
                        }
                        condition = condition.required('This field is required.');
                    }
                }
            }
        }
        validations[question === null || question === void 0 ? void 0 : question.questionKey] = condition;
    };
    for (var _i = 0, questions_1 = questions; _i < questions_1.length; _i++) {
        var question = questions_1[_i];
        _loop_1(question);
    }
    var yupSchema = Yup.object().shape(validations);
    return yupSchema;
};
